import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Typography,
  Paper,
  Box,
  Grid,
  makeStyles,
} from "@material-ui/core";
import instance from "api/globalaxios";
import SearchCaseForm from "../Components/SearchCase";
import { getCaseReferenceNumber } from "../utils";
import BookHearingRoomComponent from "../Components/BookHearingRoom";
import Swal from "sweetalert2";

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: "left",
  },
  detailsPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: 8,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  detailBox: {
    backgroundColor: "#F9FAFB",
    padding: theme.spacing(2),
    borderRadius: 6,
    border: "1px solid #EAECF0",
    marginBottom: theme.spacing(3),
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: "0.95rem",
    color: TEXT.strong,
    fontWeight: 600,
    // Long values (e.g. a CMP / MSEFC number) wrap inside their column
    // instead of running into the next one.
    overflowWrap: "anywhere",
  },
}));

const BookHearingRoomTab = ({ prefill }) => {
  const classes = useStyles();
  const [searchedCase, setSearchedCase] = useState(null);

  const handleSearchSuccess = (caseData) => {
    setSearchedCase(caseData);
  };

  const handleSearchError = (error) => {
    console.error("Search failed:", error);
    setSearchedCase(null);
  };

  const handleBookingSuccess = () => {
    Swal.fire("Success", "Hearing room booked successfully!", "success");
    setSearchedCase(null); // Reset after successful booking
  };

  // Deep-link prefill (from the Order Drafting "Post this matter" link): look up
  // the case by number/year and pre-select it, so the arbitrator lands on the
  // booking form for that case with the stage and date already chosen.
  useEffect(() => {
    if (!prefill?.case_number || !prefill?.year) return;
    const caseNumber = `${prefill.prefix || "AC"} No. ${prefill.case_number}/${prefill.year}`;
    let active = true;
    instance
      .get(
        `/bac/get-case-by-number/?case_number=${encodeURIComponent(caseNumber)}`
      )
      .then((res) => {
        if (active) setSearchedCase(res.data);
      })
      .catch((err) => {
        if (active) {
          console.error("Prefill case lookup failed:", err);
          setSearchedCase(null);
        }
      });
    return () => {
      active = false;
    };
  }, [prefill?.case_number, prefill?.year, prefill?.prefix]);

  // Stage to pre-populate. When arriving from an Order Drafting "Post this
  // matter" link (prefill present), DO NOT pre-select any stage — the drafter
  // must pick the stage they are posting the matter for. Only fall back to the
  // case's current stage for normal Book Hearing Room usage (no prefill).
  // StageInputField value maps to the arbitration stage ID (integer) or key.
  const initialStage = prefill
    ? ""
    : searchedCase?.arbitration_stage ||
      searchedCase?.arbitration_stage?.id ||
      searchedCase?.current_stage ||
      "";
  // The link carries no date either, so nothing is pre-selected there.
  const initialDate = prefill?.date || "";

  const refNo = getCaseReferenceNumber(searchedCase);

  return (
    <Box className={classes.container}>

      <Box mb={3}>
        <SearchCaseForm
          handleSearchSuccess={handleSearchSuccess}
          handleSearchError={handleSearchError}
        />
      </Box>

      {searchedCase && (
        <Paper className={classes.detailsPaper} elevation={1}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary" gutterBottom>
            Case Details: {searchedCase.tiac_custom_dispute_id}
          </Typography>

          <Box className={classes.detailBox}>
            <Grid container spacing={2}>
              {refNo && (
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.detailLabel}>{refNo.label}</Typography>
                  <Typography className={classes.detailValue}>{refNo.value}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={refNo ? 3 : 4}>
                <Typography className={classes.detailLabel}>Claimant</Typography>
                <Typography className={classes.detailValue}>
                  {searchedCase.claimant?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={refNo ? 3 : 4}>
                <Typography className={classes.detailLabel}>Respondent</Typography>
                <Typography className={classes.detailValue}>
                  {searchedCase.respondent?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={refNo ? 3 : 4}>
                <Typography className={classes.detailLabel}>Current Stage</Typography>
                <Typography className={classes.detailValue}>
                  {searchedCase.arbitration_stage || searchedCase.current_stage || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
            Available Hearing Rooms
          </Typography>

          <BookHearingRoomComponent
            disputeId={searchedCase.id}
            initialStage={initialStage}
            initialDate={initialDate}
            onBookingSuccess={handleBookingSuccess}
            isAdmin={true}
          />
        </Paper>
      )}
    </Box>
  );
};

export default BookHearingRoomTab;